import React from "react";
import { useLocation } from "react-router-dom";
import TrainerSidebar from "@/components/ui/TrainerSidebar";
import CohortSidebar from "@/components/admin-dashboard/CohortSidebar";
import TrainerHeader from "@/components/ui/TrainerHeader";
import AdminHeader from "@/components/admin-dashboard/AdminHeader";
import { useSidebarStore } from "@/store/sidebarStore";
import clsx from "clsx";

interface TrainerLayoutProps {
  children: React.ReactNode;
  title: string;
  showSidebar?: boolean;
}

const TrainerLayout: React.FC<TrainerLayoutProps> = ({ children, title, showSidebar = true }) => {
  const location = useLocation();
  const { isOpen } = useSidebarStore();

  // Check if we're on a cohort dashboard route
  const isCohortDashboardRoute =
    location.pathname.startsWith("/cohort-dashboard/");

  const renderSidebar = () => {
    if (!showSidebar) return null;

    // If we're on a cohort dashboard route, show CohortSidebar
    if (isCohortDashboardRoute) {
      return <CohortSidebar />;
    }

    return <TrainerSidebar title={title} isOpen={isOpen} />;
  };

  const renderHeader = () => {
    // For cohort dashboard routes, show AdminHeader (which will show cohort breadcrumbs)
    if (isCohortDashboardRoute) {
      return <AdminHeader />;
    }
    return <TrainerHeader title={title} />;
  };

  return (
    <div className="flex h-screen bg-gray-100 font-metropolis">
      {renderSidebar()}
      <div className={clsx(
        "flex flex-col flex-1 min-w-0",
        !showSidebar && "ml-0",
        showSidebar && isCohortDashboardRoute && "lg:pl-[72px]",
        showSidebar && !isCohortDashboardRoute && "lg:pl-[320px]"
      )}>
        {renderHeader()}
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100 p-4 2xl:p-6 h-2md:pb-0">
          {children}
        </main>
      </div>
    </div>
  );
};

export default TrainerLayout;
